/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  userAgentInterceptor,
  schemaValidateInterceptor,
} from '../helpers/interceptors';
import { HttpClient } from './HttpClient';
import {
  AxiosHandledError,
  AxiosInitOptions,
  RequestType,
} from '../types/request';
import { buildRequest } from '../helpers/requestBuilder';
export class Client extends HttpClient {
  client: AxiosInstance;

  constructor(options?: AxiosInitOptions) {
    super();
    if (options?.baseUrl) {
      this.client = axios.create({
        baseURL: options?.baseUrl,
        timeout: options?.timeout ?? 30000,
        headers: options?.headers,
        paramsSerializer: options?.paramsSerializer,
      });
    } else {
      this.client = axios;
    }
  }

  get interceptors(): AxiosInstance['interceptors'] {
    return this.client.interceptors;
  }

  addUserAgent(appName: string, appVersion: string) {
    this.client = userAgentInterceptor(this.client, appName, appVersion);
  }

  requestWithSchema<T = unknown>(
    requestProps: RequestType,
  ): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = buildRequest(requestProps);
    if (requestProps?.schema) {
      this.client = schemaValidateInterceptor(this.client, requestProps.schema);
    }
    return this.client.request<T>(config);
  }

  request<T = unknown>(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.client.request<T>(config);
  }

  get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.client.get<T>(url, config);
  }

  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.client.post<T>(url, data, config);
  }

  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.client.put<T>(url, data, config);
  }

  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.client.patch<T>(url, data, config);
  }

  delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.client.delete<T>(url, config);
  }

  handleError(error: unknown): AxiosHandledError {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with a non-2xx status code (4xx, 5xx)
        return {
          error: 'Server error',
          status: error.response.status,
          data: error.response.data ?? null, // Handle undefined data
        };
      } else if (error.request) {
        // Request was made but no response received (timeout, network error, server down)
        return {
          error: 'No response from server',
          message: error.message,
        };
      } else {
        // Error occurred in setting up the request
        return {
          error: 'Request setup error',
          message: error.message,
        };
      }
    }
    // Non-Axios error (programming errors, etc.)
    return {
      error: 'Unexpected error',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}
